import type { FC, ReactNode } from 'react';
import { useCallback, useEffect, useEffectEvent, useMemo, useRef, useState } from 'react';

import { useLocation, useSearchParams } from 'react-router';

import { SHOW_SELECTED_URL_PARAM } from '@/constants/constants';
import { useAppSelector } from '@/hooks/redux-hooks';
import { extractFiltersFromParams } from '@/pages/Catalog/utils/extractFiltersFromParams';
import { getEmptyFilterState } from '@/pages/Catalog/utils/getEmptyFilterState';

import { FilterContext } from './FilterContextBase';

import type { FilterState } from '../../types/filter';

export const FilterProvider: FC<{
  children: ReactNode;
  disableSearchParams?: boolean;
}> = ({ children, disableSearchParams = false }) => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Local state is the source of truth for UI (instant updates)
  const [localFilters, setLocalFilters] = useState<FilterState>(() =>
    disableSearchParams ? getEmptyFilterState() : extractFiltersFromParams(searchParams)
  );

  // Track the last URL params string to detect external changes
  const lastUrlParamsRef = useRef(searchParams.toString());

  // Always-current ref so effects/callbacks can read localFilters without it being a dep
  const localFiltersRef = useRef(localFilters);

  useEffect(() => {
    localFiltersRef.current = localFilters;
  }, [localFilters]);

  // Derive filters from URL params
  const urlFilters = useMemo(
    () => (disableSearchParams ? null : extractFiltersFromParams(searchParams)),
    [searchParams, disableSearchParams]
  );

  // Sync local state from URL params when URL changes externally (back/forward, links)
  // Compare URL params string to detect external changes vs internal updates
  const syncFromUrl = useEffectEvent((urlFiltersValue: FilterState) => {
    setLocalFilters(urlFiltersValue);
  });

  useEffect(() => {
    if (!disableSearchParams && urlFilters) {
      const currentUrlParams = searchParams.toString();
      const lastUrlParams = lastUrlParamsRef.current;

      if (currentUrlParams !== lastUrlParams) {
        lastUrlParamsRef.current = currentUrlParams;
        if (!areFiltersEqual(localFiltersRef.current, urlFilters)) {
          syncFromUrl(urlFilters);
        }
      }
    }
  }, [urlFilters, searchParams, disableSearchParams]);

  const isHaveEmptySymbols = useAppSelector(state => state.symbolSelector.symbols.length === 0);
  const location = useLocation();

  const updateSearchParams = useCallback(
    (updates: Record<string, string | null>) => {
      setSearchParams(params => {
        const currentParams = Object.fromEntries(params.entries());

        for (const [key, value] of Object.entries(updates)) {
          if (value === null || value === '') {
            delete currentParams[key];
          } else {
            currentParams[key] = value;
          }
        }

        // Update ref immediately to track this as an internal update
        const newParams = new URLSearchParams(currentParams);
        lastUrlParamsRef.current = newParams.toString();

        return currentParams;
      });
    },
    [setSearchParams]
  );

  const setFilter = useCallback(
    (key: string, value: string | null | boolean) => {
      // Check if toggling (clicking same value)
      const currentValue = localFiltersRef.current[key as keyof FilterState];
      const isToggling =
        key !== 'search' && value !== null && String(value) === String(currentValue);

      const newValue = isToggling ? null : value;

      // Update local state immediately (instant UI update)
      setLocalFilters(prev => ({
        ...prev,
        [key]: newValue
      }));

      // Sync to URL params in background (no delay for UI)
      if (!disableSearchParams) {
        updateSearchParams({
          [key]: newValue === null ? null : String(newValue)
        });
      }
    },
    [updateSearchParams, disableSearchParams]
  );

  const showSelectedFilter = localFilters[SHOW_SELECTED_URL_PARAM];

  // Handle clearing SHOW_SELECTED_URL_PARAM when symbols are empty
  const clearShowSelectedFilter = useEffectEvent(() => {
    setLocalFilters(prev => ({
      ...prev,
      [SHOW_SELECTED_URL_PARAM]: false
    }));
    if (!disableSearchParams) {
      updateSearchParams({
        [SHOW_SELECTED_URL_PARAM]: null
      });
    }
  });

  useEffect(() => {
    if (
      (location.pathname.includes('catalog') || location.pathname.includes('dashboard')) &&
      isHaveEmptySymbols &&
      showSelectedFilter
    ) {
      clearShowSelectedFilter();
    }
  }, [isHaveEmptySymbols, showSelectedFilter, location.pathname]);

  const clearAllFilters = useCallback(() => {
    // Clear local state immediately
    setLocalFilters(getEmptyFilterState());

    // Sync to URL params in background
    if (!disableSearchParams) {
      setSearchParams({}, { replace: true });
      // Update ref immediately to track this as an internal update
      lastUrlParamsRef.current = '';
    }
  }, [setSearchParams, disableSearchParams]);

  const isAnyFilterApplied = useMemo(() => {
    const entries = Object.entries(localFilters).filter(
      ([key]) => key !== SHOW_SELECTED_URL_PARAM && key !== 'search'
    );
    return !entries.every(([, value]) => value === null);
  }, [localFilters]);

  const value = useMemo(
    () => ({
      filters: localFilters,
      clearAllFilters,
      setFilter,
      isAnyFilterApplied
    }),
    [localFilters, clearAllFilters, setFilter, isAnyFilterApplied]
  );

  return <FilterContext value={value}>{children}</FilterContext>;
};

const areFiltersEqual = (prevFilters: FilterState, nextFilters: FilterState) => {
  const allKeys = new Set([...Object.keys(prevFilters), ...Object.keys(nextFilters)]);
  return Array.from(allKeys).every(key => {
    const prevValue = String(prevFilters[key as keyof FilterState] ?? null);
    const nextValue = String(nextFilters[key as keyof FilterState] ?? null);
    return prevValue === nextValue;
  });
};
